/**
 * Pure client-side CSV builders for Shopnomix reconciliation views.
 *
 * Reports are already buffered in memory after the /reports/campaigns
 * and /reports/daily fetches — no need to round-trip the server for a
 * streamed download. Two flavours:
 *
 *   • downloadReconciliationCsv → per-campaign summary
 *   • downloadDailyCsv          → per-day × per-publisher breakdown
 *
 * Both append the report window + cap as trailing `#`-comment rows so
 * operations can audit which payload the export was generated from.
 */

function csvEscape(v) {
  if (v === null || v === undefined) return "";
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function triggerDownload(rows, filename) {
  const blob = new Blob([rows.join("\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function downloadReconciliationCsv(report) {
  if (!report?.data?.length) return;
  const headers = [
    "campaign_label", "campaign_id", "status",
    "pubs_with_clicks", "pubs_with_revenue",
    "our_clicks", "feed_clicks", "feed_epc",
    "feed_revenue", "pub_earnings", "margin", "take_pct",
    "cap_violations", "days_with_data",
  ];
  const rows = [
    headers.join(","),
    ...report.data.map((r) => headers.map((h) => csvEscape(r[h])).join(",")),
    "",
    `# window,${report.start_date},${report.end_date}`,
    `# payout_cap_usd,${report.payout_cap_usd ?? ""}`,
    `# exported_at,${new Date().toISOString()}`,
  ];
  triggerDownload(rows, `reconciliation_${report.start_date}_to_${report.end_date}.csv`);
}

export function downloadDailyCsv(report) {
  if (!report?.data?.length) return;
  const headers = [
    "date", "campaign_label",
    "publisher_company", "publisher_email", "publisher_sub_id",
    "our_clicks", "feed_clicks", "feed_epc",
    "feed_revenue", "pub_earnings", "margin",
    "cap_fired", "pending",
  ];
  const rows = [
    headers.join(","),
    ...report.data.map((r) => headers.map((h) => csvEscape(r[h] ?? "")).join(",")),
    "",
    `# window,${report.start_date},${report.end_date}`,
    `# payout_cap_usd,${report.payout_cap_usd ?? ""}`,
    `# total_feed_revenue,${report.totals?.feed_revenue ?? 0}`,
    `# total_pub_earnings,${report.totals?.pub_earnings ?? 0}`,
    `# total_margin,${report.totals?.margin ?? 0}`,
    `# exported_at,${new Date().toISOString()}`,
  ];
  triggerDownload(rows, `reconciliation_daily_${report.start_date}_to_${report.end_date}.csv`);
}
